import { cssJoin } from '@shared/util';
import {
  type ElementType,
  forwardRef,
  type HTMLAttributes,
  type PropsWithChildren,
} from 'react';

type Props = HTMLAttributes<HTMLElement> &
  PropsWithChildren<{
    href?: string;
    color?: Color;
    className?: string;
    component?: ElementType;
    variant?:
      | 'Heading 1 - 32'
      | 'Heading 2 - 24'
      | 'Heading 3 - 24'
      | 'Title 20 - Emphasis'
      | 'Title 20 - Standard'
      | 'Body 16 - Emphasis'
      | 'Body 16 - Standard'
      | 'Body 14 - Emphasis'
      | 'Body 14 - Standard'
      | 'Micro 12 - Uppercase'
      | 'Micro 12 - Emphasis'
      | 'Micro 12 - Standard';
  }>;

const getDefaultComponent = (variant: Props['variant']): ElementType => {
  switch (variant) {
    case 'Heading 1 - 32':
      return 'h1';
    case 'Heading 2 - 24':
      return 'h2';
    case 'Heading 3 - 24':
      return 'h3';
    case 'Title 20 - Emphasis':
    case 'Title 20 - Standard':
      return 'h4';
    default:
      return 'p';
  }
};

const Typography = forwardRef<HTMLElement, Props>(
  (
    {
      href,
      color,
      children,
      className,
      component,
      variant = 'Body 14 - Standard',
      ...restProps
    },
    ref,
  ) => {
    const Component = component || getDefaultComponent(variant);

    return (
      <Component
        ref={ref}
        href={href}
        {...restProps}
        className={cssJoin(
          color && `text-${color}`,
          variant === 'Heading 1 - 32' && 'heading-1',
          variant === 'Heading 2 - 24' && 'heading-2',
          variant === 'Heading 3 - 24' && 'heading-3',
          variant.includes('Micro 12') && 'micro',
          variant.includes('Title 20') && 'font-20',
          variant.includes('Body 14') && 'font-14',
          variant.includes('Body 16') && 'font-16',
          variant.includes('Uppercase') && 'uppercase',
          variant.includes('Standard') && 'font-light',
          variant.includes('Emphasis') && 'font-medium',
          className,
        )}
      >
        {children}
      </Component>
    );
  },
);

Typography.displayName = 'Typography';

export default Typography;
